import {useEffect, useState} from "react";
import {
    GetEmployeeQuery,
    useGetEmployeeLazyQuery
} from "../../../__generated__/generated_types";
import { useProperties } from "../../../contexts/properties/PropertiesContext";
import { transposeAcquisitionDateDatabaseToDisplay } from "../../../pages/workflows/account/formula-bar/logic/utils";
import { LocalPayrollEmployeeBonusModel, LocalPayrollEmployeeRaiseModel } from "../helpers/types";

export interface EmployeeDataState {
    isDataReady: boolean;
    refreshEmployeeData: (employeeId: string) => void;
    getData: () => GetEmployeeQuery["getEmployee"]|null;
}

export function useGetEmployee(): EmployeeDataState {
    const [isDataReady, setDataReady] = useState<boolean>(false);
    const [data, setData] = useState<GetEmployeeQuery["getEmployee"]|null>(null);
    const {currentProperty} = useProperties();

    const [
        getEmployee,
        { data: employeeData, loading: employeeDataLoading, error: employeeDataError }
    ] = useGetEmployeeLazyQuery({ fetchPolicy: "network-only" });

    useEffect(
        () => {
            /*console.info(`useEffect
                isDataReady: ${isDataReady}
                employeeId: ${employeeId}
                employeeDataLoading: ${employeeDataLoading}
                employeeData: ${JSON.stringify(employeeData)}
                employeeDataError: ${employeeDataError}`);*/
            if(employeeData === undefined || employeeDataLoading){
                return;
            }

            if (employeeData.getEmployee) {
                let employeeStartDate = employeeData.getEmployee?.startDate;
                let employeeEndDate = employeeData.getEmployee?.terminationDate;
                const transposedRaises = JSON.parse(JSON.stringify(employeeData.getEmployee.raises));
                const transposedBonuses = JSON.parse(JSON.stringify(employeeData.getEmployee.bonuses));
                const transposedOvertime = JSON.parse(JSON.stringify(employeeData.getEmployee.overtime));
                const transposedCompItems = JSON.parse(JSON.stringify(employeeData.getEmployee.compItems));

                if (currentProperty?.isAcquisitionProperty) {
                    const startDate = employeeData.getEmployee.startDate;
                    const endDate = employeeData.getEmployee.terminationDate;

                    if (employeeStartDate) {
                        employeeStartDate = transposeAcquisitionDateDatabaseToDisplay(startDate, currentProperty);
                        employeeStartDate = new Date(employeeStartDate).toISOString();
                    }

                    if (employeeEndDate) {
                        employeeEndDate = transposeAcquisitionDateDatabaseToDisplay(endDate, currentProperty);
                        employeeEndDate = new Date(employeeEndDate).toISOString();
                    }

                    for (let i = 0; i < transposedRaises.length; i++) {
                        const thisRaise = transposedRaises[i];

                        if (thisRaise?.onetimeRaiseDate) {
                            thisRaise.onetimeRaiseDate = transposeAcquisitionDateDatabaseToDisplay(thisRaise.onetimeRaiseDate, currentProperty);
                        }
                    }

                    for (let i = 0; i < transposedBonuses.length; i++) {
                        const thisBonus = transposedBonuses[i];

                        if (thisBonus?.onetimeBonusDate) {
                            thisBonus.onetimeBonusDate = transposeAcquisitionDateDatabaseToDisplay(thisBonus.onetimeBonusDate, currentProperty);
                        }
                    }

                    for (let i = 0; i < transposedOvertime.length; i++) {
                        const thisOvertime = transposedOvertime[i];

                        if (thisOvertime?.effectiveStartDate) {
                            thisOvertime.effectiveStartDate = transposeAcquisitionDateDatabaseToDisplay(thisOvertime.effectiveStartDate, currentProperty);
                        }

                        if (thisOvertime?.effectiveEndDate) {
                            thisOvertime.effectiveEndDate = transposeAcquisitionDateDatabaseToDisplay(thisOvertime.effectiveEndDate, currentProperty);
                        }
                    }

                    for (let i = 0; i < transposedCompItems.length; i++) {
                        const thisCompItem = transposedCompItems[i];

                        if (thisCompItem?.effectiveStartDate) {
                            thisCompItem.effectiveStartDate = transposeAcquisitionDateDatabaseToDisplay(thisCompItem.effectiveStartDate, currentProperty);
                        }

                        if (thisCompItem?.effectiveEndDate) {
                            thisCompItem.effectiveEndDate = transposeAcquisitionDateDatabaseToDisplay(thisCompItem.effectiveEndDate, currentProperty);
                        }
                    }
                }

                setData({
                    ...employeeData.getEmployee,
                    startDate: employeeStartDate,
                    terminationDate: employeeEndDate,
                    raises: transposedRaises,
                    bonuses: transposedBonuses,
                    overtime: transposedOvertime,
                    compItems: transposedCompItems,
                });
                setDataReady(true);
            }
        },
        [employeeData, employeeDataLoading, employeeDataError]
    );

    const refreshEmployeeData = (employeeId: string) => {
        if (employeeId) {
            setDataReady(false);
            getEmployee({
                variables: {
                    employeeId: employeeId
                }
            });
        }
    };

    const getData = () => {
        return data;
    };

    return {
        isDataReady,
        refreshEmployeeData,
        getData,
    };
}
